// Mobile bottom navigation bar — iOS-style tab bar with Play, Characters, Account

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

// Crossed swords SVG for the Play tab
const SWORDS_ICON = html`<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
  <line x1="13" x2="19" y1="19" y2="13" />
  <line x1="16" x2="20" y1="16" y2="20" />
  <line x1="19" x2="21" y1="21" y2="19" />
  <polyline points="14.5 6.5 18 3 21 3 21 6 17.5 9.5" />
  <line x1="5" x2="9" y1="14" y2="18" />
  <line x1="7" x2="4" y1="17" y2="20" />
  <line x1="3" x2="5" y1="19" y2="21" />
</svg>`;

const TABS = [
  { id: 'play', label: 'Play', icon: null, svg: SWORDS_ICON, href: '/play' },
  { id: 'characters', label: 'Characters', icon: 'bi-people', svg: null, href: '/characters' },
  { id: 'account', label: 'Account', icon: 'bi-person-circle', svg: null, href: '/account' },
];

class MobileNavBar extends BaseComponent {
  static properties = {
    _activeTab: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .nav-bar {
      display: flex;
      justify-content: space-around;
      align-items: center;
      background: #ffffff;
      border-top: 1px solid #dee2e6;
      padding: 0.4rem 0;
      padding-bottom: calc(0.4rem + env(safe-area-inset-bottom));
    }

    .nav-tab {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.15rem;
      flex: 1;
      padding: 0.25rem 0;
      background: none;
      border: none;
      cursor: pointer;
      -webkit-tap-highlight-color: transparent;
    }

    .nav-tab .icon {
      font-size: 1.35rem;
      color: #8e8e93;
      transition: color 0.2s ease;
    }

    .nav-tab .svg-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .nav-tab .svg-icon svg {
      stroke: #8e8e93;
      transition: stroke 0.2s ease;
    }

    .nav-tab.active .svg-icon svg {
      stroke: #0d6efd;
    }

    .nav-tab .label {
      font-size: 0.65rem;
      color: #8e8e93;
      letter-spacing: 0.03em;
      transition: color 0.2s ease;
    }

    .nav-tab.active .icon,
    .nav-tab.active .label {
      color: #0d6efd;
    }
  `;

  constructor() {
    super();
    this._activeTab = 'play';
  }

  async connectedCallback() {
    super.connectedCallback();
    this._updateActiveTab();
    this._unsubscribeRouter = router.subscribe(() => this._updateActiveTab());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeRouter?.();
  }

  _updateActiveTab() {
    const route = router.currentRoute || '';
    if (
      route.startsWith('character') ||
      route === 'new-character' ||
      route === 'quick-create-character' ||
      route === 'custom-create-character'
    ) {
      this._activeTab = 'characters';
    } else if (route === 'account') {
      this._activeTab = 'account';
    } else {
      this._activeTab = 'play';
    }
  }

  /** @param {string} href */
  _navigate(href) {
    router.navigate(href);
  }

  render() {
    return html`
      <nav class="nav-bar">
        ${TABS.map(
          (tab) => html`
          <button
            class="nav-tab ${this._activeTab === tab.id ? 'active' : ''}"
            @click="${() => this._navigate(tab.href)}"
          >
            ${tab.svg ? html`<span class="icon svg-icon">${tab.svg}</span>` : html`<i class="icon bi ${tab.icon}"></i>`}
            <span class="label">${tab.label}</span>
          </button>
        `,
        )}
      </nav>
    `;
  }
}

customElements.define('mobile-nav-bar', MobileNavBar);
